import { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router';
import Logo from '../brand/Logo.jsx';
import Wordmark from '../brand/Wordmark.jsx';
import useCartStore, { selectCartCount } from '../../stores/cartStore.js';
import useAuthStore, { selectIsAuthenticated } from '../../stores/authStore.js';
import { useQuery } from '@tanstack/react-query';
import { keys } from '../../services/queryClient.js';
import * as cartApi from '../../services/cart.js';
import { fetchLivePromotion } from '../../services/adminPromos.js';

const NAV = [
  { to: '/new', label: 'New Releases' },
  { to: '/secondhand', label: 'Secondhand' },
  { to: '/subscription', label: 'Subscription' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
  { to: '/book-fair', label: 'Book Fair' },
];

function navClass({ isActive }) {
  return [
    'label transition-colors duration-200',
    isActive ? 'text-stamp-ink font-semibold' : 'text-ink-80 hover:text-ink',
  ].join(' ');
}

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const serverCartQuery = useQuery({
    queryKey: keys.cart,
    queryFn: cartApi.fetchCart,
    enabled: isAuthenticated,
  });

  const guestCount = useCartStore(selectCartCount);
  const cartCount = isAuthenticated
    ? (serverCartQuery.data?.itemCount ??
        serverCartQuery.data?.items?.reduce((n, i) => n + i.quantity, 0) ??
        0)
    : guestCount;

  const signedOut = useAuthStore((s) => s.status === 'anonymous');

  const { data: promoData } = useQuery({
    queryKey: ['promotions', 'live'],
    queryFn: fetchLivePromotion,
  });
  const hasLivePromotion = Boolean(promoData?.promotion);
  const navItems = NAV.filter((item) => item.to !== '/book-fair' || hasLivePromotion);

  // Close drawer on ESC key
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [open]);

  function handleSearchSubmit(e) {
    e.preventDefault();
    const q = e.target.elements.search?.value?.trim();
    setOpen(false);
    if (q) {
      navigate(`/search?q=${encodeURIComponent(q)}`);
    } else {
      navigate('/search');
    }
  }

  return (
    <header className="rule-b sticky top-0 z-40 bg-[#e7e2dc]" style={{ willChange: 'transform' }}>
      <div className="page-gutter mx-auto flex h-[4.25rem] sm:h-[4.5rem] max-w-(--container-page) items-center justify-between gap-3 sm:gap-6 md:gap-8">
        <Link
          to="/"
          className="flex shrink-0 items-center gap-2.5 sm:gap-3 no-underline"
          onClick={() => setOpen(false)}
        >
          <Logo size={28} className="sm:w-8 sm:h-8" title="Chapters&Co" />
          <Wordmark className="text-lg sm:text-xl" />
        </Link>

        {/* Desktop Primary Nav */}
        <nav aria-label="Primary" className="hidden flex-1 items-center gap-6 lg:gap-8 xl:gap-10 lg:flex">
          {navItems.filter((item) => item.to !== '/contact').map((item) => (
            <NavLink key={item.to} to={item.to} className={navClass}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Action icons / shortcuts */}
        <div className="flex items-center gap-3 sm:gap-5 md:gap-6">
          <Link
            to="/search"
            className="label text-ink-80 hover:text-ink hidden no-underline sm:block"
          >
            Search
          </Link>
          <Link
            to={signedOut ? '/login' : '/account'}
            className="label text-ink-80 hover:text-ink no-underline hidden lg:inline-block"
          >
            {signedOut ? 'Sign in / Register' : 'Account'}
          </Link>
          <Link
            to="/cart"
            className="label text-ink hover:text-stamp-ink no-underline flex items-center min-h-[44px] py-2"
          >
            <span>Cart</span>
            <span className="text-ink-60 ml-1 tabular-nums font-semibold">({cartCount})</span>
          </Link>

          {/* Mobile & Tablet hamburger / close button with accessible tap target */}
          <button
            type="button"
            className="label text-ink min-h-[44px] min-w-[44px] -mr-2 px-2.5 py-2 flex items-center justify-center lg:hidden transition-colors"
            aria-expanded={open}
            aria-controls="mobile-nav"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Close' : 'Menu'}
          </button>
        </div>
      </div>

      {/* Mobile & Tablet Drawer */}
      {open && (
        <nav
          id="mobile-nav"
          aria-label="Mobile Navigation"
          className="rule-t page-gutter bg-paper-raised lg:hidden border-b border-rule shadow-lg animate-in fade-in duration-150"
        >
          <div className="mx-auto max-w-(--container-page) py-4">
            {/* Quick Search on Mobile */}
            <form onSubmit={handleSearchSubmit} className="mb-4">
              <div className="relative flex items-center">
                <input
                  type="search"
                  name="search"
                  placeholder="Search books, authors, subjects…"
                  aria-label="Search catalog"
                  className="bg-paper border-rule text-ink placeholder:text-ink-60 focus:border-stamp focus:outline-none w-full border px-3.5 py-2.5 text-sm"
                />
                <button
                  type="submit"
                  className="label text-stamp-ink hover:text-ink absolute right-2 px-2 py-1 text-xs"
                >
                  Go &rarr;
                </button>
              </div>
            </form>

            <ul className="list-none p-0 divide-y divide-rule/60">
              {navItems.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    onClick={() => setOpen(false)}
                    className="font-display text-display-sm block py-3.5 no-underline hover:text-stamp-ink transition-colors"
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>

            {/* Account & Secondary Controls on Mobile */}
            <div className="rule-t mt-4 pt-4 space-y-3">
              <div className="flex items-center justify-between py-1">
                <Link
                  to={signedOut ? '/login' : '/account'}
                  onClick={() => setOpen(false)}
                  className="label text-ink hover:text-stamp-ink no-underline min-h-[44px] flex items-center"
                >
                  {signedOut ? 'Sign in / Register →' : 'Your Account & Orders →'}
                </Link>
                <Link
                  to="/cart"
                  onClick={() => setOpen(false)}
                  className="label text-stamp-ink font-bold no-underline min-h-[44px] flex items-center"
                >
                  Bag ({cartCount})
                </Link>
              </div>
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}
